import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { LoggerType } from "@modules/utils/loggerUtils"
import dayjs from "dayjs"
import { useFormik } from "formik"
import { ArrowLeft, CalendarDays } from "lucide-react"
import { Dispatch, SetStateAction } from "react"
import { useTheme } from "styled-components"
import { FormEvent } from "primereact/ts-helpers"
import * as Yup from "yup"

export type MemberSendOfferModalStartDateProps = {
  deferredDate: Date | null
  setDeferredDate: Dispatch<SetStateAction<Date | null>>
  setIsDeferredDateVisible: Dispatch<SetStateAction<boolean>>
} & React.HTMLAttributes<HTMLDivElement>

const deferredDateYupValidation = () =>
  Yup.object({
    defferedDate: Yup.date().nullable().min(dayjs().toDate(), word("member.send.offer.modal.startDate.errorForm")),
  })

export const MemberSendOfferModalStartDate: React.FC<MemberSendOfferModalStartDateProps> = ({
  deferredDate,
  setDeferredDate,
  setIsDeferredDateVisible,
  ...props
}) => {
  const theme = useTheme()
  const defaultClassName = "flex flex-column mt-4 w-full gap-4 flex-grow-1"

  const formik = useFormik<{ defferedDate: Date | null }>({
    initialValues: { defferedDate: deferredDate },
    validationSchema: deferredDateYupValidation(),
    onSubmit: async () => {
      try {
        setDeferredDate(formik.values.defferedDate)
        setIsDeferredDateVisible(false)
      } catch (e: any) {
        console.error(LoggerType.UI + "defferred date in send box offer", e)
      }
    },
  })

  return (
    <div className={props.className ?? defaultClassName} {...props}>
      {/* TITLE */}
      <div className="flex gap-2 align-items-center justify-content-center">
        <CalendarDays color={theme.mainWhite} strokeWidth={1} size={13} />
        <HUText fontStyle="TS" color={theme.mainWhite} className="text-center">
          {word("member.send.offer.modal.startDate.title")}
        </HUText>
      </div>
      <FormikInput
        flex
        formikStyle={{ display: "flex", flexGrow: 1 }}
        label={word("member.send.offer.modal.startDate.titleInput")}
        getFieldProps={formik.getFieldProps}
        isInvalid={isFormFieldInValid("defferedDate", formik)}
        error={getFormErrorMessage("defferedDate", formik)}
        name="defferedDate"
        type="calendar"
        calendarProps={{
          onChange: (event: FormEvent<Date>) => {
            if (event.value) {
              if (dayjs(event.value).isSame(dayjs(), "day")) {
                formik.setFieldValue("defferedDate", null)
              } else {
                formik.setFieldValue("defferedDate", event.value)
              }
            }
          },
          minDate: dayjs().startOf("day").toDate(),
          showIcon: true,
          placeholder: dayjs().format("L"),
        }}
      />

      {/* BUTTON */}
      <div className="flex flex-row flex-grow-1 gap-2 mt-2 align-items-end justify-content-center">
        <HUButton
          type="Left_Icon"
          size="M"
          colorType="Quaternary"
          text={deferredDate !== null ? word("global.reset") : word("global.cancel")}
          onClick={() => {
            setIsDeferredDateVisible(false)
            setDeferredDate(null)
          }}
          icon={{
            iconView: <ArrowLeft strokeWidth={2} color={theme.mainWhite} size={15} />,
          }}
          className="w-full"
        />
        <HUButton
          type="Right_Icon"
          size="M"
          colorType="Primary"
          text={word("global.check")}
          onClick={() => formik.handleSubmit()}
          className="w-full"
        />
      </div>
    </div>
  )
}
